<template>
  <layout-header></layout-header>
  <layout-sidebar></layout-sidebar>
  <div class="page-wrapper">
    <div class="content">
      <div
        class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3"
      >
        <breadcrumb :title="title" :text="text" :text1="text1" />
        <div class="d-flex my-xl-auto right-content align-items-center flex-wrap">
          <router-link
            to="/employee/employee-list"
            class="btn btn-white me-2 mb-2"
          >
            <i class="ti ti-users me-1"></i>All Employees
          </router-link>
        </div>
      </div>

      <div class="card">
        <div
          class="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3"
        >
          <div>
            <h5 class="mb-1">Organization Chart</h5>
            <p class="text-muted fs-13 mb-0">
              Built from each employee’s <strong>Manager</strong> on their profile.
            </p>
          </div>
          <div
            class="d-flex align-items-center flex-wrap row-gap-2 column-gap-2"
          >
            <div style="min-width: 200px">
              <vue-select
                v-model="departmentId"
                :options="departmentOptions"
                placeholder="All departments"
                :clearable="true"
                class="dq-vselect"
                :reduce="(d) => d?.id"
              />
            </div>
            <div class="input-icon-start position-relative" style="min-width: 220px">
              <span class="input-icon-addon"><i class="ti ti-search"></i></span>
              <input
                v-model.trim="searchQuery"
                type="text"
                class="form-control"
                placeholder="Search name or code"
              />
            </div>
            <div class="btn-group">
              <button
                type="button"
                class="btn btn-light btn-sm"
                :class="{ active: viewMode === 'chart' }"
                @click="viewMode = 'chart'"
              >
                Chart
              </button>
              <button
                type="button"
                class="btn btn-light btn-sm"
                :class="{ active: viewMode === 'table' }"
                @click="viewMode = 'table'"
              >
                Reporting lines
              </button>
            </div>
            <template v-if="viewMode === 'chart'">
              <div class="btn-group">
                <button
                  type="button"
                  class="btn btn-light btn-sm"
                  @click="zoomOut"
                  title="Zoom out"
                >
                  <i class="ti ti-zoom-out"></i>
                </button>
                <button
                  type="button"
                  class="btn btn-light btn-sm"
                  @click="fitToView"
                  title="Fit to screen"
                >
                  {{ Math.round(zoom * 100) }}%
                </button>
                <button
                  type="button"
                  class="btn btn-light btn-sm"
                  @click="zoomIn"
                  title="Zoom in"
                >
                  <i class="ti ti-zoom-in"></i>
                </button>
              </div>
              <button
                type="button"
                class="btn btn-outline-primary btn-sm"
                @click="collapsed = !collapsed"
              >
                {{ collapsed ? "Expand all" : "Collapse reports" }}
              </button>
            </template>
            <button
              type="button"
              class="btn btn-primary btn-sm"
              :disabled="loading"
              @click="loadTree"
            >
              <i class="ti ti-refresh me-1"></i>Refresh
            </button>
          </div>
        </div>

        <div v-if="!loading && treeMeta.total" class="px-3 pt-3">
          <div class="org-stats d-flex flex-wrap gap-2 mb-0">
            <span class="badge bg-light text-dark border">
              {{ treeMeta.total }} employees
            </span>
            <span class="badge bg-success-subtle text-success border border-success-subtle">
              {{ treeMeta.with_manager }} with a manager
            </span>
            <span
              v-if="treeMeta.without_manager"
              class="badge bg-warning-subtle text-warning border border-warning-subtle"
            >
              {{ treeMeta.without_manager }} without a manager
            </span>
          </div>

          <div
            v-if="treeMeta.without_manager > 0"
            class="alert alert-warning d-flex align-items-start gap-2 py-2 px-3 mt-3 mb-0 fs-13"
          >
            <i class="ti ti-info-circle mt-1"></i>
            <div>
              Most employees have <strong>no manager assigned</strong>, so the chart
              cannot show reporting lines yet. Open
              <router-link to="/employee/employee-list">All Employees</router-link>,
              edit a profile, and set the <strong>Manager</strong> field. People
              with direct reports appear in the tree below their manager.
            </div>
          </div>
        </div>

        <div class="card-body p-0">
          <div v-if="loading" class="text-center py-5 text-muted">
            Loading organization chart…
          </div>
          <div
            v-else-if="!roots.length"
            class="text-center py-5 text-muted px-3"
          >
            <i class="ti ti-hierarchy-2 fs-36 d-block mb-2"></i>
            <p class="mb-1">No employees to chart yet.</p>
            <p class="fs-13 mb-0">
              Add employees and assign a manager on each profile to build the
              reporting tree.
            </p>
          </div>

          <div v-else-if="viewMode === 'table'" class="p-3">
            <div class="table-responsive">
              <table class="table table-hover align-middle mb-0">
                <thead class="table-light">
                  <tr>
                    <th>Employee</th>
                    <th>Code</th>
                    <th>Designation</th>
                    <th>Reports to</th>
                    <th class="text-center">Direct reports</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="row in reportingRows"
                    :key="row.id"
                    :class="{ 'table-warning': !row.manager_name }"
                    role="button"
                    @click="openEmployee(row)"
                  >
                    <td>
                      <span
                        class="report-indent"
                        :style="{ paddingLeft: `${row.depth * 18}px` }"
                      >
                        {{ row.full_name }}
                      </span>
                    </td>
                    <td>{{ row.employee_id }}</td>
                    <td>{{ row.designation || "—" }}</td>
                    <td>
                      <span v-if="row.manager_name">{{ row.manager_name }}</span>
                      <span v-else class="text-muted">Not set</span>
                    </td>
                    <td class="text-center">
                      <span
                        v-if="row.reports?.length"
                        class="badge bg-primary-subtle text-primary"
                      >
                        {{ row.reports.length }}
                      </span>
                      <span v-else class="text-muted">0</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div v-else ref="scrollRef" class="org-chart-scroll">
            <div
              v-if="managerTrees.length"
              class="org-section px-2 pt-2"
            >
              <h6 class="org-section__title">Reporting structure</h6>
              <div
                class="org-chart-fit"
                :style="{
                  width: '100%',
                  height: naturalHeight ? `${naturalHeight * zoom}px` : 'auto',
                }"
              >
                <div
                  ref="stageRef"
                  class="org-chart-stage"
                  :style="{ transform: `scale(${zoom})` }"
                >
                  <ul class="org-forest">
                    <org-tree-node
                      v-for="node in managerTrees"
                      :key="node.id"
                      :node="node"
                      :search="searchQuery"
                      :force-collapse="collapsed"
                      @open="openEmployee"
                      @layout-change="scheduleFit"
                    />
                  </ul>
                </div>
              </div>
            </div>

            <div v-if="orphanEmployees.length" class="org-section px-3 pb-3">
              <h6 class="org-section__title mt-3">
                No manager assigned ({{ orphanEmployees.length }})
              </h6>
              <p class="text-muted fs-13 mb-3">
                These employees are not linked to a manager yet. They appear at
                the top level until you assign one.
              </p>
              <div class="org-orphans-grid">
                <button
                  v-for="node in orphanEmployees"
                  :key="node.id"
                  type="button"
                  class="org-orphan-card"
                  @click="openEmployee(node)"
                >
                  <div class="org-orphan-card__name">{{ node.full_name }}</div>
                  <div class="org-orphan-card__meta">
                    {{ node.designation || "No designation" }}
                  </div>
                  <div class="org-orphan-card__code">{{ node.employee_id }}</div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from "vue";
import { useRouter } from "vue-router";
import { useEmployeeStore } from "@/stores/employeeStore";
import { useDepartmentStore } from "@/stores/departmentStore";
import OrgTreeNode from "@/components/hrm/org-tree-node.vue";

const title = "Organization Chart";
const text = "Employees";
const text1 = "Organization Chart";

const router = useRouter();
const employeeStore = useEmployeeStore();
const departmentStore = useDepartmentStore();

const roots = ref([]);
const treeMeta = ref({
  total: 0,
  with_manager: 0,
  without_manager: 0,
  roots: 0,
});
const loading = ref(false);
const departmentId = ref(null);
const searchQuery = ref("");
const viewMode = ref("chart");
const zoom = ref(1);
const collapsed = ref(false);
const scrollRef = ref(null);
const stageRef = ref(null);
const naturalHeight = ref(0);

let resizeObserver = null;
let fitTimer = null;

const departmentOptions = computed(() =>
  (departmentStore.departments || []).map((d) => ({
    id: d.id,
    text: d.name,
    label: d.name,
  }))
);

const managerTrees = computed(() =>
  roots.value.filter((node) => (node.reports?.length || 0) > 0)
);

const orphanEmployees = computed(() =>
  roots.value.filter(
    (node) => !node.manager_id && !(node.reports?.length || 0)
  )
);

function flattenNodes(nodes, depth = 0) {
  const rows = [];
  for (const node of nodes) {
    rows.push({ ...node, depth });
    if (node.reports?.length) {
      rows.push(...flattenNodes(node.reports, depth + 1));
    }
  }
  return rows;
}

const reportingRows = computed(() => {
  const structured = flattenNodes(managerTrees.value);
  const orphans = orphanEmployees.value.map((node) => ({ ...node, depth: 0 }));
  return [...structured, ...orphans].sort((a, b) =>
    String(a.full_name || "").localeCompare(String(b.full_name || ""))
  );
});

async function loadTree() {
  loading.value = true;
  try {
    const params = {};
    if (departmentId.value) params.department_id = departmentId.value;
    const payload = await employeeStore.fetchOrgTree(params);
    roots.value = Array.isArray(payload?.data) ? payload.data : [];
    treeMeta.value = {
      total: payload?.meta?.total || 0,
      with_manager: payload?.meta?.with_manager || 0,
      without_manager: payload?.meta?.without_manager || 0,
      roots: payload?.meta?.roots || roots.value.length,
    };
  } catch {
    roots.value = [];
    treeMeta.value = {
      total: 0,
      with_manager: 0,
      without_manager: 0,
      roots: 0,
    };
  } finally {
    loading.value = false;
    scheduleFit();
  }
}

function openEmployee(node) {
  if (!node?.id) return;
  router.push({
    path: "/employee/employee-details",
    query: { id: String(node.id) },
  });
}

function measureStage() {
  const stage = stageRef.value;
  if (!stage) return;
  zoom.value = 1;
  naturalHeight.value = stage.offsetHeight;
}

async function fitToView() {
  await nextTick();
  const container = scrollRef.value;
  const stage = stageRef.value;
  if (!container || !stage || viewMode.value !== "chart") return;

  measureStage();
  await nextTick();

  const padding = 24;
  const availableHeight = Math.max(container.clientHeight - padding, 280);
  if (!naturalHeight.value) return;

  if (naturalHeight.value <= availableHeight) {
    zoom.value = 1;
  } else {
    const fitScale = (availableHeight / naturalHeight.value) * 0.97;
    zoom.value = Math.max(0.25, Number(fitScale.toFixed(3)));
  }

  await nextTick();
  container.scrollTop = 0;
}

function scheduleFit() {
  if (fitTimer) window.clearTimeout(fitTimer);
  fitTimer = window.setTimeout(() => {
    fitToView();
  }, 80);
}

function zoomIn() {
  zoom.value = Math.min(1.5, Number((zoom.value + 0.1).toFixed(2)));
}

function zoomOut() {
  zoom.value = Math.max(0.2, Number((zoom.value - 0.1).toFixed(2)));
}

watch(departmentId, () => {
  loadTree();
});

watch(collapsed, () => {
  scheduleFit();
});

watch(viewMode, () => {
  if (viewMode.value === "chart") scheduleFit();
});

onMounted(async () => {
  await departmentStore.fetchDepartments().catch(() => {});
  await loadTree();

  if (scrollRef.value && typeof ResizeObserver !== "undefined") {
    resizeObserver = new ResizeObserver(() => scheduleFit());
    resizeObserver.observe(scrollRef.value);
  }
});

onBeforeUnmount(() => {
  if (resizeObserver) resizeObserver.disconnect();
  if (fitTimer) window.clearTimeout(fitTimer);
});
</script>

<style scoped>
.org-stats .badge {
  font-weight: 500;
}

.org-chart-scroll {
  overflow: auto;
  padding: 0 0 1.25rem;
  min-height: 320px;
  max-height: min(72vh, 820px);
  background:
    radial-gradient(circle at top left, rgba(254, 138, 34, 0.06), transparent 40%),
    #f8f9fb;
  border-radius: 0 0 0.5rem 0.5rem;
}

.org-section__title {
  font-size: 0.82rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #64748b;
  margin-bottom: 0.75rem;
}

.org-chart-fit {
  margin: 0 auto;
  position: relative;
  max-width: 100%;
}

.org-chart-stage {
  transform-origin: top center;
  width: 100%;
}

.org-forest {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
  list-style: none;
  margin: 0 auto;
  padding: 0 0 1rem;
  width: 100%;
}

.org-orphans-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 0.75rem;
}

.org-orphan-card {
  border: 1px dashed #d1d5db;
  background: #fff;
  border-radius: 0.65rem;
  padding: 0.65rem 0.75rem;
  text-align: left;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.org-orphan-card:hover {
  border-color: #fe8a22;
  box-shadow: 0 4px 12px rgba(254, 138, 34, 0.12);
}

.org-orphan-card__name {
  font-size: 0.82rem;
  font-weight: 600;
  color: #1f2937;
}

.org-orphan-card__meta,
.org-orphan-card__code {
  font-size: 0.7rem;
  color: #6b7280;
}

.report-indent {
  display: inline-block;
}

@media (max-width: 767.98px) {
  .org-chart-scroll {
    min-height: 280px;
    max-height: 65vh;
  }
}
</style>
